import { composeClassNames, getImageUrl } from "@faedev/editor-utils";
import Link from "next/link";
import { CSSProperties } from "react";
import { Col, Image, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { ReactElement } from "react-markdown/lib/react-markdown";
import Heading from "src/components/Heading";
import Paragraph from "src/components/Paragraph";
import Translate from "src/components/Translate";
import constants from "src/constants";
import gutter from "src/functions/gutter";
import { sLogotypes } from "src/styles/sLogotypes.module.scss";
import { CSSTextAlign } from "src/types/CSSTextAlign";

type Props = {
  align?: CSSTextAlign | null | undefined;
  bgColor?:
    | ("light" | "grey" | "dark" | "white" | "primary" | "transparent")
    | null
    | undefined;
  color?:
    | ("light" | "grey" | "dark" | "white" | "primary" | "original")
    | null
    | undefined;
  columns?: {
    sm: number;
    md: number;
    lg: number;
  };
  className?: (string | string) | null | undefined;
  data: {
    alt?: string | null | undefined;
    id: string;
    icon?: ReactElement;
    image?: string | ReactElement;
    minWidth?: string | null | undefined;
    path: string;
    tooltip?: string | null | undefined;
  }[];
  description?: string | null | undefined;
  size?: ("sm" | "lg") | null | undefined;
  style?: CSSProperties | null | undefined;
  showText?: boolean;
  title?: string | null | undefined;
};

const COLUMNS_COUNT = 12;

function Logotypes({
  align,
  bgColor = "transparent",
  color = "original",
  columns = {
    sm: 4,
    md: 4,
    lg: 5,
  },
  data,
  description = "",
  className,
  size = "sm",
  style,
  showText = false,
  title = "",
}: Props) {
  const texts = (title || description) && (
    <>
      {title && title.length > 0 && (
        <Heading
          type="h3"
          className="logotypes-heading"
          title={<Translate tKey={title} />}
        />
      )}
      {description && description.length > 0 && (
        <Paragraph size="xn" className="logotypes-description">
          <Translate tKey={description} />
        </Paragraph>
      )}
    </>
  );

  const images =
    data &&
    data.map((logotype) => {
      const element = (
        <div
          className="logotype"
          key={`logo-${logotype.id}`}
          style={{ minWidth: logotype.minWidth || "none" }}
        >
          <Link key={logotype.id} href={logotype.path} passHref>
            <a target="_blank">
              {logotype?.image ? (
                typeof logotype.image === "string" ? (
                  <Image
                    alt={logotype.alt || ""}
                    className="logotype-image"
                    src={getImageUrl(logotype.image)}
                    style={{ minWidth: logotype.minWidth || "none" }}
                  />
                ) : (
                  logotype.image
                )
              ) : (
                logotype?.icon && (
                  <span
                    className="logotype-image"
                    style={{ minWidth: logotype.minWidth || "none" }}
                  >
                    {logotype.icon}
                  </span>
                )
              )}
            </a>
          </Link>
        </div>
      );
      return (
        (logotype.tooltip && (
          <OverlayTrigger
            placement="top"
            delay={constants.Intervals.overlayDelay}
            overlay={
              <Tooltip id="table-item-info">
                <Paragraph
                  size="sm"
                  width={`regular`}
                  align="center"
                  style={gutter(`margin`, `horizontal`, `sm`)}
                >
                  <Translate tKey={logotype.tooltip} />
                </Paragraph>
              </Tooltip>
            }
          >
            {element}
          </OverlayTrigger>
        )) ||
        element
      );
    });

  return (
    <div
      className={composeClassNames([
        sLogotypes,
        align,
        `color-${color}`,
        `bgColor-${bgColor}`,
        size,
        className,
      ])}
      style={style}
      id="logotypes"
    >
      {showText ? (
        <Row>
          <Col
            xs={COLUMNS_COUNT}
            sm={columns.sm}
            md={columns.md}
            lg={columns.lg}
            className="logotypes-label"
          >
            {texts}
          </Col>
          <Col
            xs={COLUMNS_COUNT}
            sm={COLUMNS_COUNT - columns.sm}
            md={COLUMNS_COUNT - columns.md}
            lg={COLUMNS_COUNT - columns.lg}
            className="logotypes-list"
          >
            {images}
          </Col>
        </Row>
      ) : (
        <>{images}</>
      )}
    </div>
  );
}

export default Logotypes;
